import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useSearchParams} from 'next/navigation'
import {useLinkResolver} from 'prismicio'
import type {FC} from 'react'
import {useMemo} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {IconTag} from 'src/common/IconTag'
import type {Context} from 'src/common/types'

/**
 * Props for `ExploreProductOptions`.
 */
export type ExploreProductOptionsProps = SliceComponentProps<Content.ExploreProductOptionsSlice> & {context: Context}

/**
 * Component for "ExploreProductOptions" Slices.
 */
const ExploreProductOptions: FC<ExploreProductOptionsProps> = ({slice, context}) => {
  const {linkResolver} = useLinkResolver()
  const searchParams = useSearchParams()
  const shouldFilter = searchParams.get('explore-all') !== 'true'

  const filteredOptions = useMemo(() => {
    if (!isFilled.group(slice.primary.options)) {
      return []
    }

    if (shouldFilter) {
      return slice.primary.options.filter((option) => option.always_shown)
    } else {
      return slice.primary.options
    }
  }, [slice.primary.options, shouldFilter])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pb-16 pt-[148px] md:py-20 md:pt-[144px]"
      backgroundColor="Grey/100"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 p-5 md:gap-16 md:p-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-2 md:gap-6">
          {isFilled.group(slice.primary.ratings) && (
            <div className="w-fit">
              <IconTag
                version="v1"
                iconName={slice.primary.ratings[0].review_platform}
                label={slice.primary.ratings[0].label as string}
                iconContainerColor="Moss Black"
              />
            </div>
          )}
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              heading1: ({children}) => (
                <h1 className="text-[32px] font-medium leading-[56px] text-moss-black md:text-[48px]">{children}</h1>
              ),
            }}
          />
        </div>
        <div
          className={cn('grid gap-x-6 max-md:gap-y-6 md:grid-cols-2', {
            'grid-rows-[repeat(2,231px)] gap-y-6': filteredOptions.length === 3,
            'grid-rows-1': filteredOptions.length === 2,
          })}
        >
          {filteredOptions.map((item, index) => (
            <div
              key={`option-${index}`}
              className={cn('relative grid gap-6 rounded-lg bg-moss-white p-4 md:p-6 ', {
                'row-span-2': index === 0,
                'grid-cols-2': index !== 0 && filteredOptions.length !== 2,
              })}
            >
              <div className="flex flex-col gap-4">
                <div className="flex w-full flex-col gap-2">
                  <div
                    className={cn('flex gap-2', {
                      'flex-col-reverse items-start': index !== 0 && filteredOptions.length !== 2,
                      'items-center': index === 0,
                    })}
                  >
                    <PrismicRichText
                      field={item.title}
                      components={{
                        heading3: ({children}) => (
                          <h2 className="text-[24px] font-medium leading-[38px] text-moss-black">{children}</h2>
                        ),
                      }}
                    />
                  </div>
                  <PrismicRichText
                    field={item.description}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-tg-body-m max-w-[340px] text-moss-grey-500">{children}</p>
                      ),
                    }}
                  />
                </div>
                <ButtonV2
                  variant="primary"
                  className="!h-[32px] w-min"
                  href={linkResolver(item.button)}
                  trackingData={{
                    mktgId: item.type || '',
                    location: `explore-product-options-${index}-button`,
                    dataType: 'explore-product-options-button',
                    pageName: context.pageUid,
                  }}
                  newTrackingProps={{
                    interaction: {
                      element: `explore-product-options-${item.type}`,
                      elementText: item.button.text,
                      location: `explore-product-options-${index}-button`,
                    },
                    pageType: context.pageUid,
                  }}
                >
                  {item.button.text}
                </ButtonV2>
              </div>
              {isFilled.imageThumbnail(item.image.mobile) && (
                <PrismicNextImage field={item.image.mobile} className="md:hidden" />
              )}
              <PrismicNextImage
                field={item.image}
                className={cn('w-full rounded-[4px]', {
                  'max-md:hidden': isFilled.imageThumbnail(item.image.mobile),
                })}
              />
              <div className="pointer-events-none absolute inset-0 rounded-lg border border-moss-black/[10%]" />
            </div>
          ))}
        </div>
      </div>
    </Container>
  )
}

export default ExploreProductOptions
